import { useRef, useEffect, useState } from 'react'
import { Environment } from '@react-three/drei'
import { Model } from './modelLoader.jsx'
import { InstancedForest } from './instancedForest.jsx'
import { Rain } from './Rain.jsx'
import { useCamera, CameraController } from './SimpleCameraSystem.jsx'
import { useMusicPlayer } from './MusicPlayerContext.jsx'

export default function SimpleExperience({ audioEnabled = true, onSceneReady }) {
  const ambientLightRef = useRef()
  const spotLightRef = useRef()
  const spotLightTargetRef = useRef()
  const rainRef = useRef()
  const { sceneRefs, currentCameraState } = useCamera()
  const { GLOBAL_AUDIO_SCALE } = useMusicPlayer()
  const [sceneLoaded, setSceneLoaded] = useState(false)
  const [environmentIntensity, setEnvironmentIntensity] = useState(0.05) // Start at outdoor intensity
  
  // HTML Audio refs (no AudioContext creation)
  const outdoorRainAudioRef = useRef()
  const indoorRainAudioRef = useRef()
  const doorSoundRef = useRef()
  const [audioInitialized, setAudioInitialized] = useState(false)
  const [previousCameraState, setPreviousCameraState] = useState('outside')

  // Register refs with camera system and add environment light control function
  useEffect(() => {
    Object.assign(sceneRefs.current, {
      rainComponent: rainRef.current,
      // Function to immediately start environment light transition
      startLightTransition: (targetState) => {
        // Determine target intensity based on destination
        const isIndoorPosition = ['lounge', 'office', 'gallery', 'music', 'about', 'contact'].includes(targetState)
        const targetIntensity = isIndoorPosition ? 1 : 0.05
        
        const currentIntensity = environmentIntensity
        if (currentIntensity !== targetIntensity) {
          console.log(`Starting immediate environment light transition to ${targetIntensity} for state: ${targetState}`)
          
          // Animation matching camera duration (2 seconds)
          let startTime = Date.now()
          const duration = 1000 // Match camera animation duration
          const startIntensity = currentIntensity
          
          const animate = () => {
            const elapsed = Date.now() - startTime
            const progress = Math.min(elapsed / duration, 1)
            
            // Smooth easing function (power2.inOut to match GSAP)
            const easeProgress = progress < 0.5 
              ? 2 * progress * progress 
              : 1 - Math.pow(-2 * progress + 2, 2) / 2
            
            const newIntensity = startIntensity + (targetIntensity - startIntensity) * easeProgress
            setEnvironmentIntensity(newIntensity)
            
            if (progress < 1) {
              requestAnimationFrame(animate)
            }
          }
          
          animate()
        }
      },
      // Function to play door sound immediately
      playDoorSound: () => {
        if (doorSoundRef.current && audioInitialized) {
          console.log('Playing door sound immediately at camera movement start')
          doorSoundRef.current.currentTime = 0 // Reset to beginning
          doorSoundRef.current.play().catch(e => console.log('Door sound play failed:', e))
        }
      }
    })
  }, [sceneRefs, audioInitialized, environmentIntensity])

  // Set up spotlight target properly
  useEffect(() => {
    if (spotLightRef.current && spotLightTargetRef.current) {
      spotLightRef.current.target = spotLightTargetRef.current
      spotLightRef.current.target.updateMatrixWorld()
    }
  }, [])
  
  // Weather settings
  const isRaining = true
  const rainCount = 800

  // Audio transition helper functions  
  const fadeIn = (audio, targetVolume = 0.25 * GLOBAL_AUDIO_SCALE, duration = 1000) => {
    if (!audio) return
    
    audio.volume = 0
    audio.play().catch(e => console.log('Audio play failed:', e))
    
    const startTime = Date.now()
    const fadeInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Smooth fade curve
      audio.volume = targetVolume * progress
      
      if (progress >= 1) {
        clearInterval(fadeInterval)
        console.log(`Fade in complete: ${audio.src}`)
      }
    }, 16) // ~60fps
  }
  
  const fadeOut = (audio, duration = 1000) => {
    if (!audio) return
    
    const startVolume = audio.volume
    const startTime = Date.now()
    
    const fadeInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Smooth fade curve
      audio.volume = startVolume * (1 - progress)
      
      if (progress >= 1) {
        audio.pause()
        clearInterval(fadeInterval)
        console.log(`Fade out complete: ${audio.src}`)
      }
    }, 16) // ~60fps
  }
  
  const crossFade = (fadeOutAudio, fadeInAudio, targetVolume = 0.25 * GLOBAL_AUDIO_SCALE, duration = 1500) => {
    if (!fadeOutAudio || !fadeInAudio) return
    
    console.log('Starting crossfade between audio tracks')
    
    const startVolumeOut = fadeOutAudio.volume
    const startTime = Date.now()
    
    // Start the new audio at 0 volume
    fadeInAudio.volume = 0
    fadeInAudio.play().catch(e => console.log('Audio play failed:', e))
    
    const crossFadeInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Smooth crossfade curves
      fadeOutAudio.volume = startVolumeOut * (1 - progress)
      fadeInAudio.volume = targetVolume * progress
      
      if (progress >= 1) {
        fadeOutAudio.pause()
        clearInterval(crossFadeInterval)
        console.log('Crossfade complete')
      }
    }, 16) // ~60fps
  }

  // Initialize HTML audio when user enables audio
  useEffect(() => {
    if (!audioEnabled || audioInitialized) return

    console.log('Creating HTML audio elements after user gesture')
    
    // Create HTML audio elements (no AudioContext)
    const outdoorAudio = new window.Audio('/audio/effects/rain-outdoor-thunder-1.mp3')
    const indoorAudio = new window.Audio('/audio/effects/indoor-rain-thunder-1.mp3')
    const doorSound = new window.Audio('/audio/effects/sound-door-1.0.mp3') // Use existing door sound file
    
    outdoorAudio.loop = true
    indoorAudio.loop = true
    doorSound.loop = false // Door sound plays once
    outdoorAudio.volume = 0 // Start at 0 for fade-in
    indoorAudio.volume = 0 // Start at 0 for fade-in
    doorSound.volume = 1.5 * GLOBAL_AUDIO_SCALE // Door sound volume at 100% with global scaling
    
    outdoorRainAudioRef.current = outdoorAudio
    indoorRainAudioRef.current = indoorAudio
    doorSoundRef.current = doorSound
    
    setAudioInitialized(true)
    
    // Start outdoor rain with fade-in since we start outside
    if (isRaining && (currentCameraState === 'outside' || currentCameraState === 'doorbell')) {
      console.log('Starting outdoor rain audio with fade-in')
      fadeIn(outdoorAudio, 0.25 * GLOBAL_AUDIO_SCALE, 2000) // 2 second fade-in with global scaling
    }
  }, [audioEnabled, audioInitialized, currentCameraState, isRaining])

  // Control rain visibility based on camera position
  useEffect(() => {
    if (!rainRef.current) return
    
    // Show rain when outside or at doorbell, hide when inside (lounge or other indoor positions)
    const shouldShowRain = currentCameraState === 'outside' || currentCameraState === 'doorbell'
    
    console.log(`Camera state: ${currentCameraState}, Rain visibility: ${shouldShowRain}`)
    rainRef.current.setIsRaining(shouldShowRain)
  }, [currentCameraState])

  // Report scene as ready immediately (don't wait for audio)
  useEffect(() => {
    if (!sceneLoaded) {
      // Scene is ready as soon as component mounts
      const timer = setTimeout(() => {
        setSceneLoaded(true)
        onSceneReady?.()
        console.log('Scene reported as ready')
      }, 100)
      
      return () => clearTimeout(timer)
    }
  }, [sceneLoaded, onSceneReady])

  // Rain audio switching with smooth crossfades (door sound now handled at camera movement start)
  useEffect(() => {
    if (!audioInitialized || !audioEnabled) return
    
    const isInside = ['lounge', 'office', 'gallery', 'music', 'about', 'contact'].includes(currentCameraState)
    const isOutside = ['outside', 'doorbell'].includes(currentCameraState)
    
    // Door sound is now handled at the start of camera movement in SimpleCameraSystem.jsx
    // This effect now only handles rain audio transitions
    
    // Update previous state for next transition
    setPreviousCameraState(currentCameraState)
    
    // Handle rain audio if raining
    if (!isRaining || !outdoorRainAudioRef.current || !indoorRainAudioRef.current) return
    
    console.log(`Rain switching: ${currentCameraState}, isInside: ${isInside}, isOutside: ${isOutside}`)
    
    if (isInside) {
      // Crossfade from outdoor to indoor
      if (!outdoorRainAudioRef.current.paused && indoorRainAudioRef.current.paused) {
        console.log('Crossfading from outdoor to indoor rain')
        crossFade(outdoorRainAudioRef.current, indoorRainAudioRef.current, 0.25 * GLOBAL_AUDIO_SCALE, 1500) // 1.5s crossfade with global scaling
      } else if (indoorRainAudioRef.current.paused) {
        // No outdoor playing, just fade in indoor
        console.log('Fading in indoor rain')
        fadeIn(indoorRainAudioRef.current, 0.25 * GLOBAL_AUDIO_SCALE, 1000)
      }
    } else if (isOutside) {
      // Crossfade from indoor to outdoor
      if (!indoorRainAudioRef.current.paused && outdoorRainAudioRef.current.paused) {
        console.log('Crossfading from indoor to outdoor rain')
        crossFade(indoorRainAudioRef.current, outdoorRainAudioRef.current, 0.25 * GLOBAL_AUDIO_SCALE, 1500) // 1.5s crossfade with global scaling
      } else if (outdoorRainAudioRef.current.paused) {
        // No indoor playing, just fade in outdoor
        console.log('Fading in outdoor rain')
        fadeIn(outdoorRainAudioRef.current, 0.25 * GLOBAL_AUDIO_SCALE, 1000) // With global scaling
      }
    }
  }, [currentCameraState, isRaining, audioInitialized, audioEnabled, previousCameraState])

  // Stop all rain audio when rain is disabled with smooth fade-out
  useEffect(() => {
    if (!isRaining && audioInitialized && audioEnabled) {
      if (outdoorRainAudioRef.current && !outdoorRainAudioRef.current.paused) {
        console.log('Fading out outdoor rain')
        fadeOut(outdoorRainAudioRef.current, 2000) // 2 second fade-out
      }
      if (indoorRainAudioRef.current && !indoorRainAudioRef.current.paused) {
        console.log('Fading out indoor rain')
        fadeOut(indoorRainAudioRef.current, 2000) // 2 second fade-out
      }
    }
  }, [isRaining, audioInitialized, audioEnabled])

  // Stop all audio when audioEnabled is turned off
  useEffect(() => {
    if (!audioEnabled && audioInitialized) {
      console.log('Audio disabled - stopping all background audio')
      
      // Stop rain audio
      if (outdoorRainAudioRef.current && !outdoorRainAudioRef.current.paused) {
        console.log('Stopping outdoor rain due to audio toggle')
        fadeOut(outdoorRainAudioRef.current, 1000) // 1 second fade-out
      }
      if (indoorRainAudioRef.current && !indoorRainAudioRef.current.paused) {
        console.log('Stopping indoor rain due to audio toggle')
        fadeOut(indoorRainAudioRef.current, 1000) // 1 second fade-out
      }
      
      // Stop door sound if playing
      if (doorSoundRef.current && !doorSoundRef.current.paused) {
        doorSoundRef.current.pause()
        doorSoundRef.current.currentTime = 0
      }
    }
  }, [audioEnabled, audioInitialized])





  return (
    <>
      {/* Camera Controller - handles all R3F camera logic */}
      <CameraController />
      
      {/* Environment map - Apartment HDR for atmospheric lighting */}
      {sceneLoaded && (
        <Environment 
          preset="apartment" 
          background={false}
          environmentIntensity={environmentIntensity}
        />
      )}
      
      {/* Lighting */}
      {/* <ambientLight ref={ambientLightRef} intensity={0.2} /> */}
      <spotLight 
        ref={spotLightRef}
        position={[0, 2.5, 5.2]}
        intensity={10}
        angle={0.2}
        penumbra={1}
        decay={1.1}
        distance={1000}
        color="rgba(255, 213, 168, 1)"
      />
      {/* Spotlight target - invisible object positioned where light should point */}
      <object3D ref={spotLightTargetRef} position={[0, -22, 0]} />
      
      {/* Fog for atmospheric depth */}
      <fog attach="fog" args={['rgba(1, 9, 24, 1)', 5, 20]} />
      
      {/* Scene objects */}
      <Model audioMuted={!audioEnabled} />
      <InstancedForest count={150} />
      <Rain ref={rainRef} isRaining={isRaining} count={rainCount} />
    </>
  )
}